import { VERTICAL_ACCEL } from '../../shared/constants.js';
import { t } from '../i18n.js';
import type { GameSnapshot } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import {
  createPitchIndicator,
  createRollIndicator,
  updatePitchIndicator as updatePitchIndicatorDial,
  updateRollIndicator as updateRollIndicatorDial
} from '../ballastIndicators.js';
import {
  appendDialNotches,
  bindHoldRepeatButton,
  clamp,
  createCard
} from '../controllerUiHelpers.js';

type CreateBallastRoomControllerOptions = {
  controls: HTMLDivElement | null;
  leftBottom: HTMLDivElement | null;
  rightAux: HTMLDivElement | null;
  onEmit: (action: string, value: number) => void;
};

const BALLAST_STEP = 0.1;
/** Step then round to the step grid so repeated 0.1 additions never drift (0.30000000000000004). */
const stepBallast = (value: number, delta: number): number =>
  Math.round(clamp(value + delta, -1, 1) / BALLAST_STEP) * BALLAST_STEP;

const moveToward = (current: number, target: number, maxDelta: number): number => {
  const diff = target - current;
  if (Math.abs(diff) <= maxDelta) return target;
  return current + Math.sign(diff) * maxDelta;
};

type GaugeRefs = {
  actualNeedle: HTMLDivElement;
  targetNeedle: HTMLDivElement;
  neutralLine: HTMLDivElement;
  valueLabel: HTMLElement;
};

export const createBallastRoomController = ({
  controls,
  leftBottom,
  rightAux,
  onEmit
}: CreateBallastRoomControllerOptions) => {
  let frontWaterLevel = 0;
  let rearWaterLevel = 0;
  let frontTargetSetting = 0;
  let rearTargetSetting = 0;
  let frontTargetDirty = false;
  let rearTargetDirty = false;

  let frontGauge: GaugeRefs | null = null;
  let rearGauge: GaugeRefs | null = null;

  // Pitch silhouette refs
  let pitchGroup: SVGGElement | null = null;
  let pitchLabel: HTMLElement | null = null;

  // Roll silhouette refs
  let rollGroup: SVGGElement | null = null;
  let rollLabel: HTMLElement | null = null;

  const toDialAngle = (value: number): number => clamp(value, -1, 1) * 90;

  const createBallastGauge = (
    label: string,
    subtitle: string
  ): { wrap: HTMLElement } & GaugeRefs => {
    const wrap = document.createElement('div');
    wrap.className = 'pilot-throttle-gauge-card';

    const dial = document.createElement('div');
    dial.className = 'pilot-throttle-dial';

    const scale = document.createElement('div');
    scale.className = 'pilot-throttle-scale';

    const center = document.createElement('div');
    center.className = 'pilot-throttle-center';

    const labelEl = document.createElement('div');
    labelEl.className = 'pilot-throttle-dial-label';
    labelEl.textContent = label;

    const subtitleEl = document.createElement('div');
    subtitleEl.className = 'pilot-throttle-dial-subtitle';
    subtitleEl.textContent = subtitle;

    const targetNeedle = document.createElement('div');
    targetNeedle.className = 'pilot-throttle-needle target ballast-gauge-needle';

    const actualNeedle = document.createElement('div');
    actualNeedle.className = 'pilot-throttle-needle actual ballast-gauge-needle';

    const neutralLine = document.createElement('div');
    neutralLine.className = 'ballast-neutral-line';
    neutralLine.style.transform = `translateX(-100%) translateY(-50%) rotate(0deg)`;

    dial.append(scale, targetNeedle, actualNeedle, neutralLine, center, labelEl, subtitleEl);
    appendDialNotches(dial);
    wrap.append(dial);

    // Value readout below gauge
    const valueLabel = document.createElement('div');
    valueLabel.className = 'ballast-tank-value-label';
    valueLabel.textContent = '0%';
    wrap.append(valueLabel);

    return { wrap, actualNeedle, targetNeedle, neutralLine, valueLabel };
  };

  const updateGauge = (
    refs: GaugeRefs,
    targetSetting: number,
    actualSetting: number,
    neutralAngle: number
  ): void => {
    refs.actualNeedle.style.transform = `translateX(-100%) translateY(-50%) rotate(${toDialAngle(actualSetting)}deg)`;
    refs.targetNeedle.style.transform = `translateX(-100%) translateY(-50%) rotate(${toDialAngle(targetSetting)}deg)`;
    refs.neutralLine.style.transform = `translateX(-100%) translateY(-50%) rotate(${neutralAngle}deg)`;
  };

  /**
   * Compute the dial angle (degrees) at which ballast achieves neutral buoyancy
   * given the current flood weight modifier. At modifier=0 the neutral point is 0
   * (centre of the gauge). As flooding increases the line moves toward +90° (rise).
   */
  const getNeutralBuoyancyAngle = (floodWeightModifier: number): number => {
    if (floodWeightModifier <= 0) return 0;
    if (floodWeightModifier >= 1) return 90;
    const neutralBallast = floodWeightModifier / (1 - floodWeightModifier);
    return clamp(neutralBallast, -1, 1) * 90;
  };

  const updatePitchIndicator = (pitchDeg: number): void => updatePitchIndicatorDial(pitchGroup, pitchLabel, pitchDeg);
  const updateRollIndicator = (rollDeg: number): void => updateRollIndicatorDial(rollGroup, rollLabel, rollDeg);

  /** Build just the DIVE / RISE button pair for a tank zone panel. */
  const buildTankButtons = (
    label: string,
    onDive: () => void,
    onRise: () => void
  ): HTMLDivElement => {
    const wrap = document.createElement('div');
    wrap.className = 'ballast-tank-btn-panel';

    const titleEl = document.createElement('div');
    titleEl.className = 'ballast-tank-btn-panel-title';
    titleEl.textContent = label;
    wrap.append(titleEl);

    const btnRow = document.createElement('div');
    btnRow.className = 'ballast-tank-btn-row';

    const diveBtn = document.createElement('button');
    diveBtn.type = 'button';
    diveBtn.className = 'controller-plusminus-button ballast-tank-dive-btn';
    diveBtn.textContent = t('dive');
    diveBtn.title = 'Increase ballast (dive)';
    bindHoldRepeatButton(diveBtn, onDive);

    const riseBtn = document.createElement('button');
    riseBtn.type = 'button';
    riseBtn.className = 'controller-plusminus-button ballast-tank-rise-btn';
    riseBtn.textContent = t('rise');
    riseBtn.title = 'Decrease ballast (rise)';
    bindHoldRepeatButton(riseBtn, onRise);

    btnRow.append(riseBtn, diveBtn);
    wrap.append(btnRow);
    return wrap;
  };

  const render = (): void => {
    const sub = controllerState.lastSnapshot?.submarine;
    const currentFront = sub?.frontBallastInput ?? 0;
    const currentRear = sub?.rearBallastInput ?? 0;
    const floodMod = sub?.floodWeightModifier ?? 0;
    const currentPitch = sub?.pitch ?? 0;
    const currentRoll = sub?.roll ?? 0;

    frontTargetSetting = currentFront;
    rearTargetSetting = currentRear;
    frontTargetDirty = false;
    rearTargetDirty = false;

    const frontFill = clamp(1 - ((currentFront + 1) / 2), 0, 1);
    const rearFill = clamp(1 - ((currentRear + 1) / 2), 0, 1);
    frontWaterLevel = frontFill;
    rearWaterLevel = rearFill;

    const onFrontDive = () => {
      frontTargetSetting = stepBallast(frontTargetSetting, -BALLAST_STEP);
      frontTargetDirty = true;
      const neutralAngle = getNeutralBuoyancyAngle(
        controllerState.lastSnapshot?.submarine.floodWeightModifier ?? 0
      );
      if (frontGauge) updateGauge(frontGauge, frontTargetSetting, clamp(1 - 2 * frontWaterLevel, -1, 1), neutralAngle);
      onEmit('ballast_adjust_front', frontTargetSetting);
    };
    const onFrontRise = () => {
      frontTargetSetting = stepBallast(frontTargetSetting, BALLAST_STEP);
      frontTargetDirty = true;
      const neutralAngle = getNeutralBuoyancyAngle(
        controllerState.lastSnapshot?.submarine.floodWeightModifier ?? 0
      );
      if (frontGauge) updateGauge(frontGauge, frontTargetSetting, clamp(1 - 2 * frontWaterLevel, -1, 1), neutralAngle);
      onEmit('ballast_adjust_front', frontTargetSetting);
    };
    const onRearDive = () => {
      rearTargetSetting = stepBallast(rearTargetSetting, -BALLAST_STEP);
      rearTargetDirty = true;
      const neutralAngle = getNeutralBuoyancyAngle(
        controllerState.lastSnapshot?.submarine.floodWeightModifier ?? 0
      );
      if (rearGauge) updateGauge(rearGauge, rearTargetSetting, clamp(1 - 2 * rearWaterLevel, -1, 1), neutralAngle);
      onEmit('ballast_adjust_rear', rearTargetSetting);
    };
    const onRearRise = () => {
      rearTargetSetting = stepBallast(rearTargetSetting, BALLAST_STEP);
      rearTargetDirty = true;
      const neutralAngle = getNeutralBuoyancyAngle(
        controllerState.lastSnapshot?.submarine.floodWeightModifier ?? 0
      );
      if (rearGauge) updateGauge(rearGauge, rearTargetSetting, clamp(1 - 2 * rearWaterLevel, -1, 1), neutralAngle);
      onEmit('ballast_adjust_rear', rearTargetSetting);
    };

    // ── Middle controls zone: gauges (top) + pitch indicator (bottom) ───────
    if (controls) {
      controls.innerHTML = '';
      controls.classList.add('ballast-role-layout');

      // Gauge row
      const gaugeRow = document.createElement('div');
      gaugeRow.className = 'ballast-gauge-row';

      const fg = createBallastGauge('FORE', 'fore tank');
      frontGauge = { actualNeedle: fg.actualNeedle, targetNeedle: fg.targetNeedle, neutralLine: fg.neutralLine, valueLabel: fg.valueLabel };
      const neutralAngle = getNeutralBuoyancyAngle(floodMod);
      const frontActual = clamp(1 - 2 * frontWaterLevel, -1, 1);
      updateGauge(frontGauge, frontTargetSetting, frontActual, neutralAngle);
      frontGauge.valueLabel.textContent = `${Math.round(currentFront * 100)}%`;

      const rg = createBallastGauge('AFT', 'aft tank');
      rearGauge = { actualNeedle: rg.actualNeedle, targetNeedle: rg.targetNeedle, neutralLine: rg.neutralLine, valueLabel: rg.valueLabel };
      const rearActual = clamp(1 - 2 * rearWaterLevel, -1, 1);
      updateGauge(rearGauge, rearTargetSetting, rearActual, neutralAngle);
      rearGauge.valueLabel.textContent = `${Math.round(currentRear * 100)}%`;

      gaugeRow.append(fg.wrap, rg.wrap);
      controls.append(gaugeRow);

      // Pitch + Roll indicators side by side below gauges
      const pitchIndicator = createPitchIndicator();
      pitchGroup = pitchIndicator.group;
      pitchLabel = pitchIndicator.label;
      updatePitchIndicator(currentPitch);

      const rollIndicator = createRollIndicator();
      rollGroup = rollIndicator.group;
      rollLabel = rollIndicator.label;
      updateRollIndicator(currentRoll);

      const pitchCard = createCard('Trim', '');
      pitchCard.classList.add('ctrl-room-main-card', 'ballast-pitch-card');
      pitchCard.append(pitchIndicator.wrap);

      const rollCard = createCard('Roll', '');
      rollCard.classList.add('ctrl-room-main-card', 'ballast-pitch-card');
      rollCard.append(rollIndicator.wrap);

      const indicatorsRow = document.createElement('div');
      indicatorsRow.className = 'ballast-indicators-row';
      indicatorsRow.append(pitchCard, rollCard);
      controls.append(indicatorsRow);
    }

    // ── FORE buttons → left bottom zone ─────────────────────────────────────
    if (leftBottom) {
      leftBottom.innerHTML = '';
      leftBottom.append(buildTankButtons('FORE', onFrontDive, onFrontRise));
    }

    // ── AFT buttons → right aux zone ─────────────────────────────────────────
    if (rightAux) {
      rightAux.innerHTML = '';
      rightAux.append(buildTankButtons('AFT', onRearDive, onRearRise));
    }
  };

  const updatePanel = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) return;

    const snapshotFront = clamp(snapshot.submarine.frontBallastInput ?? 0, -1, 1);
    const snapshotRear = clamp(snapshot.submarine.rearBallastInput ?? 0, -1, 1);
    const floodMod = snapshot.submarine.floodWeightModifier ?? 0;
    const pitchDeg = snapshot.submarine.pitch ?? 0;
    const rollDeg = snapshot.submarine.roll ?? 0;

    if (!frontTargetDirty || Math.abs(snapshotFront - frontTargetSetting) < 0.01) {
      frontTargetSetting = snapshotFront;
      frontTargetDirty = false;
    }
    if (!rearTargetDirty || Math.abs(snapshotRear - rearTargetSetting) < 0.01) {
      rearTargetSetting = snapshotRear;
      rearTargetDirty = false;
    }

    const targetFrontFill = clamp(1 - (snapshotFront + 1) / 2, 0, 1);
    const targetRearFill = clamp(1 - (snapshotRear + 1) / 2, 0, 1);
    frontWaterLevel = clamp(moveToward(frontWaterLevel, targetFrontFill, VERTICAL_ACCEL), 0, 1);
    rearWaterLevel = clamp(moveToward(rearWaterLevel, targetRearFill, VERTICAL_ACCEL), 0, 1);

    const frontActual = clamp(1 - 2 * frontWaterLevel, -1, 1);
    const rearActual = clamp(1 - 2 * rearWaterLevel, -1, 1);
    const neutralAngle = getNeutralBuoyancyAngle(floodMod);

    if (frontGauge) {
      updateGauge(frontGauge, frontTargetSetting, frontActual, neutralAngle);
      frontGauge.valueLabel.textContent = `${Math.round(snapshotFront * 100)}%`;
    }
    if (rearGauge) {
      updateGauge(rearGauge, rearTargetSetting, rearActual, neutralAngle);
      rearGauge.valueLabel.textContent = `${Math.round(snapshotRear * 100)}%`;
    }

    updatePitchIndicator(pitchDeg);
    updateRollIndicator(rollDeg);
  };

  /**
   * Externally sync target needles (e.g. if controller.ts adjusts combined trim).
   * @param front  New front ballast target (−1…1)
   * @param rear   New rear ballast target (−1…1)
   */
  const setControlTarget = (front: number, rear: number): void => {
    frontTargetSetting = clamp(front, -1, 1);
    rearTargetSetting = clamp(rear, -1, 1);
    frontTargetDirty = true;
    rearTargetDirty = true;
    const frontActual = clamp(1 - 2 * frontWaterLevel, -1, 1);
    const rearActual = clamp(1 - 2 * rearWaterLevel, -1, 1);
    const floodMod = controllerState.lastSnapshot?.submarine.floodWeightModifier ?? 0;
    const neutralAngle = getNeutralBuoyancyAngle(floodMod);
    if (frontGauge) updateGauge(frontGauge, frontTargetSetting, frontActual, neutralAngle);
    if (rearGauge) updateGauge(rearGauge, rearTargetSetting, rearActual, neutralAngle);
  };

  /** Legacy single-value telemetry shim — kept for compatibility. */
  const setTelemetryValue = (_text: string): void => {
    // Values are now managed internally via valueLabel in each gauge card.
  };

  return {
    render,
    updatePanel,
    setControlTarget,
    setTelemetryValue
  };
};
